import { unwrapResult } from '@reduxjs/toolkit'
import { Col, Modal, Row, Spin } from 'antd'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import React, { ReactElement, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootDispatch } from '../../../app/store'
import { getPrescriptionDetailAsync } from '../PharmacyRetailSlice'
import styles from './PrescriptionQueryList.module.css'
import { getGenderName } from '../../../models/user'
import { EditableList } from '../../../compnents/list/EditableList'
import { Columns } from './column'
import { selectSettings } from '../../user/setting/settingSlice'
import moment from 'moment'
import { DateTimeFormatSimpleOnlyOne } from '../../../models/datetime'

interface PrescriptionDetailModalProps {
  visible: boolean
  recipeId?: number | string
  onCancel: () => void
}

export const PrescriptionDetailModal = ({
  visible,
  recipeId,
  onCancel,
}: PrescriptionDetailModalProps): ReactElement => {
  const dispatch = useDispatch<RootDispatch>()

  const settings = useSelector(selectSettings)

  const [detail, setDetail] = useState<any>()

  const [loading, setLoading] = useState(false)

  const [showVisitDetail, setShowVisitDetail] = useState(false)

  const getStatus = (status: any) => {
    if (status == 0) {
      return '待审核'
    }
    if (status == 1) {
      return '通过'
    }
    if (status == 2) {
      return '不通过'
    }
  }

  useEffect(() => {
    if (visible && recipeId) {
      setLoading(true)
      setDetail(undefined)
      dispatch(getPrescriptionDetailAsync({ recipeId }))
        .then(unwrapResult)
        .then((v: any) => {
          setDetail(v)
        })
        .catch(() => {
          setDetail(undefined)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [visible, recipeId])

  const order = detail?.order
  const treatment = detail?.treatment
  const recipeItems = detail?.recipeItems || []

  useEffect(() => {
    if(!visible) {
      setShowVisitDetail(false)
    }
  }, [visible])

  return (
    <Modal
      visible={visible}
      title="处方详情"
      width={1100}
      footer={null}
      onCancel={onCancel}
      destroyOnClose
      bodyStyle={{ padding: '12px 24px' }}
    >
      <Spin spinning={loading}>
        <Row>
          <Col span={13} style={{ height: '600px', overflow: 'hidden', overflowY: 'auto', paddingRight: '15px' }}>
            {/* 患者申请 */}
            <div className={styles.title}>患者申请</div>
            <Row gutter={16} className={styles.row}>
              <Col span={24}>
                <span style={{ marginRight: '10px' }}>患者姓名: {order?.patientName || '-'}</span>
                <span style={{ marginRight: '10px' }}>{getGenderName(order?.sex) || '-'}</span>
                <span style={{ marginRight: '10px' }}>{order?.phone || '-'}</span>
                <span style={{ marginRight: '10px' }}>{order?.idCard || '-'}</span>
              </Col>
            </Row>
            {/* 就诊信息 */}
            <div className={styles.title} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>就诊信息</span>
              <span
                style={{ cursor: 'pointer', color: '#1890ff', fontSize: '14px', fontWeight: 'normal' }}
                onClick={() => setShowVisitDetail(!showVisitDetail)}
              >
                {showVisitDetail ? '合并详情' : '展开详情'} {showVisitDetail ? <UpOutlined /> : <DownOutlined />}
              </span>
            </div>
            <Row gutter={16} className={styles.row}>
              <Col span={24}>诊断: {treatment?.disease || '-'}</Col>
              <Col span={24}>
                <span style={{ marginRight: '10px' }}>审方药师: {order?.reviewerPharmacistName || '-'}</span>
                <span style={{ marginRight: '10px' }}>{order?.reviewerPharmacistPhone || '-'}</span>
                <span style={{ marginRight: '10px' }}>{order?.reviewerPharmacistIdCardNumber || '-'}</span>
              </Col>
              <Col span={12}>调配人员: {order?.allocateUserName || '-'}</Col>
              <Col span={12}>发药人: {order?.shipmentsName || '-'}</Col>
              {showVisitDetail && (
                <>
                  <Col span={24}>主诉: {treatment?.chiefComplaint || '-'}</Col>
                  <Col span={24}>既往史: {treatment?.pastMedicalHistory || '-'}</Col>
                  <Col span={24}>过敏史: {treatment?.allergen || '-'}</Col>
                  <Col span={24}>体格检查: {treatment?.patientTemperature &&
                    '体温：' + treatment?.patientTemperature + '℃，'}
                    {treatment?.patientSystolicPressure &&
                      '血压：' + treatment?.patientSystolicPressure + '/'}
                    {treatment?.patientDiastolicPressure &&
                      treatment?.patientDiastolicPressure + 'mmHg，'}
                    {treatment?.patientPulse &&
                      '脉搏：' + treatment?.patientPulse + '次/分，'}
                    {treatment?.patientHeight &&
                      '身高：' + treatment?.patientHeight + 'cm，'}
                    {treatment?.patientWeight &&
                      '体重：' + treatment?.patientWeight + 'kg，'}
                    {treatment?.patientWeight && treatment?.patientHeight
                      ? 'BMI：' +
                      (
                        (treatment?.patientWeight /
                          (treatment?.patientHeight *
                            treatment?.patientHeight)) *
                        10000
                      ).toFixed(1) +
                      '，'
                      : ''}
                    {treatment?.patientWaistline &&
                      '腰围：' + treatment?.patientWaistline + 'cm，'}
                    {treatment?.physicalExamination}</Col>
                  <Col span={24}>注意事项: {order?.precautions || '-'}</Col>
                  <Col span={24}>备注: {order?.remark || '-'}</Col>
                </>
              )}
            </Row>
            {/* 处方信息 */}
            <div className={styles.title}>处方信息</div>
            <Row gutter={16} className={styles.row}>
              <Col span={12}>开单时间: {order?.prescribeTime ? moment(order?.prescribeTime).format(DateTimeFormatSimpleOnlyOne) : '-'}</Col>
              <Col span={12}>审核时间: {order?.auditTime ? moment(order?.auditTime).format(DateTimeFormatSimpleOnlyOne) : '-'}</Col>
              <Col span={12}>审核人: {order?.auditPharmacistName || '-'}</Col>
              <Col span={12}>审核结果: {getStatus(Number(order?.status)) || '-'}</Col>
              {Number(order?.status) == 2 && (
                <Col span={24}>不通过原因: {order?.reason || '-'}</Col>
              )}
            </Row>
            <div className={styles.title}>处方明细</div>
            <EditableList
              columns={Columns(settings)}
              dataSource={recipeItems || []}
              pagination={false}
            />
          </Col>
          <Col span={11}>
            {order?.base64PdfStr ? (
              <iframe
                title="处方笺"
                src={`data:application/pdf;base64,${order.base64PdfStr}#view=Fit&toolbar=0&navpanes=0`}
                style={{ width: '100%', height: 600, border: 'none' }}
              />
            ) : (
              <div style={{ textAlign: 'center', lineHeight: '600px', color: '#999' }}>
                第三方处方笺
              </div>
            )}
          </Col>
        </Row>
      </Spin>
    </Modal>
  )
}
